import { workflowTreePathKey } from "./workflow-step-filter.ts";

type CreatedEntry = { createdAt: string | null; key: string };
type OrderedStep = { createdAt: string | null; pageOrdinal?: number | null; stepId: string; stepShortName: string; tree?: Array<{ key: string }> };

function naturalCompare(a: string, b: string): number {
  const aParts = a.match(/\d+|\D+/g) ?? [];
  const bParts = b.match(/\d+|\D+/g) ?? [];
  const compareRaw = (left: string, right: string): number => left < right ? -1 : left > right ? 1 : 0;
  for (let index = 0; index < Math.max(aParts.length, bParts.length); index += 1) {
    const aPart = aParts[index];
    const bPart = bParts[index];
    if (aPart === undefined) return -1;
    if (bPart === undefined) return 1;
    if (/^\d+$/.test(aPart) && /^\d+$/.test(bPart)) {
      const aDigits = aPart.replace(/^0+(?=\d)/, "");
      const bDigits = bPart.replace(/^0+(?=\d)/, "");
      if (aDigits.length !== bDigits.length) return aDigits.length < bDigits.length ? -1 : 1;
      const numericDifference = compareRaw(aDigits, bDigits);
      if (numericDifference !== 0) return numericDifference;
      const rawDifference = compareRaw(aPart, bPart);
      if (rawDifference !== 0) return rawDifference;
    } else {
      const difference = compareRaw(aPart, bPart);
      if (difference !== 0) return difference;
    }
  }
  return compareRaw(a, b);
}

function comparePath(a: readonly string[], b: readonly string[]): number {
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const aPart = a[index];
    const bPart = b[index];
    if (aPart === undefined) return -1;
    if (bPart === undefined) return 1;
    const difference = naturalCompare(aPart, bPart);
    if (difference !== 0) return difference;
  }
  return 0;
}

/** General event ordering keeps timestamps; step ordering below uses immutable identity. */
export function compareWorkflowCreation(a: CreatedEntry, b: CreatedEntry): number {
  const aTime = Date.parse(a.createdAt ?? "");
  const bTime = Date.parse(b.createdAt ?? "");
  const aOrder = Number.isFinite(aTime) ? aTime : Infinity;
  const bOrder = Number.isFinite(bTime) ? bTime : Infinity;
  if (aOrder !== bOrder) return aOrder < bOrder ? -1 : 1;
  return naturalCompare(a.key, b.key);
}

export function compareWorkflowStepsByStablePosition(a: Pick<OrderedStep, "pageOrdinal" | "stepId">, b: Pick<OrderedStep, "pageOrdinal" | "stepId">): number {
  const aOrdinal = typeof a.pageOrdinal === "number" && Number.isSafeInteger(a.pageOrdinal) ? a.pageOrdinal : null;
  const bOrdinal = typeof b.pageOrdinal === "number" && Number.isSafeInteger(b.pageOrdinal) ? b.pageOrdinal : null;
  if (aOrdinal !== null && bOrdinal !== null && aOrdinal !== bOrdinal) return aOrdinal < bOrdinal ? -1 : 1;
  if (aOrdinal !== null && bOrdinal === null) return -1;
  if (aOrdinal === null && bOrdinal !== null) return 1;
  return naturalCompare(a.stepId, b.stepId);
}

export function compareWorkflowStepsByCreation(a: Pick<OrderedStep, "pageOrdinal" | "stepId">, b: Pick<OrderedStep, "pageOrdinal" | "stepId">): number {
  return compareWorkflowStepsByStablePosition(a, b);
}

export function compareWorkflowStepsNewestFirst(a: Pick<OrderedStep, "createdAt" | "stepId">, b: Pick<OrderedStep, "createdAt" | "stepId">): number {
  const aDated = Number.isFinite(Date.parse(a.createdAt ?? ""));
  const bDated = Number.isFinite(Date.parse(b.createdAt ?? ""));
  if (aDated !== bDated) return aDated ? -1 : 1;
  return compareWorkflowCreation({ createdAt: b.createdAt, key: b.stepId }, { createdAt: a.createdAt, key: a.stepId });
}

/** Anchor each branch to its first immutable logical descendant, including filtered-out steps. */
export function workflowTreeOrder(steps: readonly OrderedStep[]): (a: string, b: string) => number {
  const pathByKey = new Map<string, string[]>();
  for (const step of steps) {
    const path = step.tree?.length ? step.tree.map((node) => node.key) : [step.stepShortName];
    for (let depth = 1; depth <= path.length; depth++) {
      const key = workflowTreePathKey(path.slice(0, depth));
      if (!pathByKey.has(key)) pathByKey.set(key, path.slice(0, depth));
    }
  }
  return (a, b) => {
    const pathDifference = comparePath(pathByKey.get(a) ?? [a], pathByKey.get(b) ?? [b]);
    if (pathDifference !== 0) return pathDifference;
    return naturalCompare(a, b);
  };
}
